import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import client from '../api/client';

/**
 * Paginated, server-side searchable product catalog.
 * Replaces client-side filtering of the old buyer_products / seller_products arrays.
 *
 * Usage:
 *   const catalog = useProductCatalog(permitdata);
 *   catalog.search('term', 'seller_category');   // debounce in caller
 *   catalog.loadPage(2);
 */
export function useProductCatalog(permitdata) {
  const shopId = permitdata?.shop_id;

  const [searchTerm, setSearchTerm] = useState('');
  const [relationType, setRelationType] = useState('buyer_category');
  const [hasSearched, setHasSearched] = useState(false);

  // Matches the original guard (`if (!permitdata?.shop_id) return;`) — guests have
  // shop_id === '-' (truthy), so search must still work for them, unlike useLoadConfig
  // which is shop-dashboard-only and excludes '-'.
  const enabled = !!shopId && hasSearched;

  const { data, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ['products', shopId, relationType, searchTerm],
    queryFn: async ({ pageParam }) => {
      const formData = new FormData();
      formData.append('shop_id', shopId);
      formData.append('relation_type', relationType);
      formData.append('search', searchTerm);
      formData.append('page', String(pageParam));

      const response = await client.post('/api/product/common/productcatalog', formData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data.status !== 'OK') {
        throw new Error(response.data.message || 'productcatalog failed');
      }
      return {
        products: response.data.products ?? [],
        total: response.data.total ?? 0,
        page: pageParam,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.page + 1,
    enabled,
  });

  const products = useMemo(() => {
    if (!enabled) return [];
    const seen = new Set();
    const result = [];
    for (const p of data?.pages ?? []) {
      for (const item of p.products) {
        const id = item.shopitem_id || item.productitem_id;
        if (seen.has(id)) continue;
        seen.add(id);
        result.push(item);
      }
    }
    return result;
  }, [data, enabled]);

  const total = enabled ? (data?.pages?.[data.pages.length - 1]?.total ?? 0) : 0;
  const page = enabled ? data?.pages?.length || 1 : 1;

  const search = useCallback((term, relType = 'buyer_category') => {
    setSearchTerm(term);
    setRelationType(relType);
    setHasSearched(true);
  }, []);

  const loadPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const clear = useCallback(() => {
    setHasSearched(false);
    setSearchTerm('');
    setRelationType('buyer_category');
  }, []);

  return { products, total, page, loading: isFetching, search, loadPage, clear };
}
